import {inject, Injectable} from '@angular/core';
import {formatNumber} from '@angular/common';
import {LanguageService} from '../../language/language.service';

@Injectable({
    providedIn: 'root'
})
export class NumberMappingService {
    private readonly languageService: LanguageService = inject(LanguageService);

    /**
     * Map a given localised string value (e.g. German format '1.200,99') to a number.
     * Removes the thousand separators (.) and replaces the decimal separator (,) with a dot.
     * @param stringValue - The localised string value that should be parsed to a number.
     * @returns The mapped number. Undefined will be returned if the given string cannot be parsed to a number.
     */
    public mapLocalizedStringToNumber(stringValue: string): number | undefined {
        if (!stringValue) {
            return undefined;
        }

        // Use Number() rather than parseFloat() so trailing garbage (e.g. '1:23') fails to parse
        // instead of being silently truncated to a valid number. Callers rely on undefined to fall
        // back to the raw input, which the form validator then flags as invalid.
        const numberValue = Number(this.normalizeLocalizedString(stringValue));

        return Number.isNaN(numberValue) ? undefined : numberValue;
    }

    /**
     * Map a given string value to a number.
     * @param stringValue - The string value that should be parsed to a number.
     * @returns The mapped number. Undefined will be returned if the given string cannot be parsed to a number.
     */
    public mapStringToNumber(stringValue: string): number | undefined {
        if (!stringValue) {
            return undefined;
        }

        const numberValue = Number(stringValue);
        if (Number.isNaN(numberValue)) {
            return undefined;
        }

        return numberValue;
    }

    /**
     * Use this function when a number should be parsed by a given format.
     * @param numberValue - The number value that should be parsed by the given format.
     * @param format - The format the number value should be parsed to. E.g. '#,0.00 €' or '#,0.####'. If emitted or empty, the number will simply be converted to a locale string.
     * @param useBrowserLocale - Use the browser locale for formatting instead of the user locale. Defaults to false.
     * @returns The parsed string.
     */
    public mapNumberToFormattedString(numberValue: number, format: string | undefined | null, useBrowserLocale: boolean = false): string {
        const locale = this.resolveLocale(useBrowserLocale);

        if (!format) {
            return numberValue.toLocaleString(locale);
        }

        const formatParts = /^([^#0,.]*)([#0,.]+)([^#0,.]*)$/
            .exec(format);
        if (!formatParts) {
            return numberValue.toLocaleString(locale);
        }

        const [, numberStringPrefix, numberPattern, numberStringSuffix] = formatParts;

        const [integerPattern, decimalPart = ''] = numberPattern.split('.');
        const mandatoryDecimalDigits = decimalPart.replaceAll(/[^0]/g, '').length;
        // If the format has a number prefix/suffix, we assume it is a currency. Therefore, we always use min 2 fraction digits.
        const minFractionDigits = numberStringPrefix || numberStringSuffix ? Math.max(2, mandatoryDecimalDigits) : mandatoryDecimalDigits;
        const maxFractionDigits = Math.max(minFractionDigits, decimalPart.length);
        const minIntegerDigits = Math.min(Math.max(integerPattern.replaceAll(/[^0]/g, '').length, 1), 21);

        let formattedNumberString: string;
        try {
            formattedNumberString = new Intl.NumberFormat(locale, {
                useGrouping: integerPattern.includes(','),
                minimumIntegerDigits: minIntegerDigits,
                minimumFractionDigits: minFractionDigits,
                maximumFractionDigits: maxFractionDigits
            })
                .format(numberValue);
        } catch {
            formattedNumberString = formatNumber(numberValue, locale, `1.${minFractionDigits}-${maxFractionDigits}`);
        }

        return `${numberStringPrefix}${formattedNumberString}${numberStringSuffix}`;
    }

    /**
     * Swap the decimal separator of a canonical dot-decimal string to match the browser locale.
     * @param canonicalValue - The canonical tag value (e.g. '1234.56').
     * @returns The value with the locale-appropriate decimal separator (e.g. '1234,56' in German).
     */
    public formatCanonicalNumberStringForLocale(canonicalValue: string | null | undefined): string {
        if (!canonicalValue) {
            return '';
        }

        return canonicalValue.replace('.', this.getLocaleDecimalSeparator());
    }

    /**
     * Convert a localised number string (e.g. '1,23' in German, '1.23' in English) to a canonical dot-decimal string.
     * An invalid number value is returned unchanged.
     * @param stringValue - The localised raw string.
     * @returns The canonical dot-decimal string, or null if the stringValue is empty.
     */
    public mapLocalizedStringToCanonicalNumberString(stringValue: string | null | undefined): string | null {
        if (!stringValue) {
            return null;
        }

        const numberValue = this.mapLocalizedStringToNumber(stringValue);
        return numberValue === undefined ? stringValue : String(numberValue);
    }

    /**
     * Get the file size string by total number of bytes. It will be mapped to the largest fitting unit (KB/MB/...).
     * @param sizeInBytes
     * @param useBinaryFormat - A boolean indicator whether the binary format should be used to calculate the file size string. The default value is false.
     * @param useBrowserLocale - Use the browser locale for formatting instead of the user locale. Defaults to false.
     * @returns A string including the file size and the corresponding unit.
     */
    public mapToFileSizeString(sizeInBytes: number, useBinaryFormat: boolean = false, useBrowserLocale: boolean = false): string {
        const baseNumber = useBinaryFormat ? 1024 : 1000;
        const fileSizeUnits = [
            'B',
            'KB',
            'MB',
            'GB',
            'TB'
        ];
        for (let unitIndex = 0; unitIndex < fileSizeUnits.length; unitIndex++) {
            const unitInBytes = Math.pow(baseNumber, unitIndex + 1);
            if (sizeInBytes <= unitInBytes) {
                return formatNumber((sizeInBytes / unitInBytes) * baseNumber, this.resolveLocale(useBrowserLocale), '1.0-2') + ' ' + fileSizeUnits[unitIndex];
            }
        }

        return sizeInBytes + ' B';
    }

    private normalizeLocalizedString(stringValue: string): string {
        const dotIndex = stringValue.indexOf('.');
        const commaIndex = stringValue.indexOf(',');

        if (dotIndex !== -1 && commaIndex !== -1) {
            // Both separators present — whichever appears last is the decimal separator.
            const lastDotIndex = stringValue.lastIndexOf('.');
            const lastCommaIndex = stringValue.lastIndexOf(',');
            return lastDotIndex > lastCommaIndex
                   ? stringValue.replaceAll(',', '')                              // English: '1,200.99'
                   : stringValue.replaceAll('.', '')
                       .replace(',', '.');                                        // German: '1.200,99'
        }

        const decimalSeparator = this.getLocaleDecimalSeparator();

        if (commaIndex !== -1) {
            if (decimalSeparator === ',') {
                return stringValue.replace(',', '.'); // German decimal comma: '0,5'
            }

            return /^-?\d{1,3}(,\d{3})+$/.test(stringValue)
                   ? stringValue.replaceAll(',', '')  // English thousands-only: '1,200'
                   : stringValue.replace(',', '.');   // Lenient fallback: '1,20' → '1.20'
        }

        if (dotIndex !== -1) {
            if (decimalSeparator === '.') {
                return stringValue;                                               // English decimal dot: '5.5', '1.200' → 1.2
            }

            return /^-?\d{1,3}(\.\d{3})+$/.test(stringValue)
                   ? stringValue.replaceAll('.', '')                              // German thousands-only: '1.200'
                   : stringValue;                                                 // Lenient fallback: '1.2' stays '1.2'
        }

        return stringValue; // Integer: '42'
    }

    private getLocaleDecimalSeparator(): string {
        const parts = new Intl.NumberFormat(this.resolveLocale(true)).formatToParts(1.1);
        return parts.find(part => part.type === 'decimal')?.value ?? '.';
    }

    private resolveLocale(useBrowserLocale: boolean): string {
        if (useBrowserLocale) {
            return this.languageService.getCurrentBrowserIntlLocale().baseName;
        }

        const currentIntlLocale = this.languageService.getCurrentIntlLocale() ?? this.languageService.getDefaultLocale();
        return currentIntlLocale.baseName;
    }
}
